import {Injectable} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationExtras, Params, Router} from '@angular/router';
import {Location} from '@angular/common';
import {StorageService} from '../storage/storage.service';
import {cloneDeep} from 'lodash-es';
import {filter, map, mergeMap} from 'rxjs/operators';
import {Period} from '../../generated/sales-terminal-client';
import {Observable} from 'rxjs';
import {SnackService} from '../snack/snack.service';

export interface RouteHeader {
  title: string;
  date: Period;
}

@Injectable()
export class RouteService {
  public static STORAGE_ROUTE_IS_PURCHASE_VIEW = 'routeService.isPurchaseView';
  public static STORAGE_WINDOW_OPENED = 'routeService.isWindowOpened';

  public windowReference: Window;
  public header: RouteHeader = {title: '', date: null};
  private currentUrl = '';
  private previousUrl = '';
  private currentParams: Params = {};

  private _isWindowOpen: boolean;
  get isWindowOpen(): boolean {
    return this._isWindowOpen;
  }
  set isWindowOpen(value: boolean) {
    this._isWindowOpen = value;
    this.storageService.set(RouteService.STORAGE_WINDOW_OPENED, value);
  }

  constructor(private router: Router, public activatedRoute: ActivatedRoute, private storageService: StorageService, private location: Location) {
    this.currentUrl = router.url;
    this.currentParams = activatedRoute.snapshot.params;
    if (storageService.get(RouteService.STORAGE_WINDOW_OPENED)) {
      this.isWindowOpen = true;
    }
    router.events.subscribe((route: any) => {
      if (route instanceof NavigationEnd) {
        this.storageService.remove(SnackService.SNACK_FIXED_MESSAGE);
        this.currentUrl = route.urlAfterRedirects || route.url;
        if (!this.isWindow) {
          if (this.checkRoute(['sales-terminal', 'search', 'event'])) {
            this.storageService.set(RouteService.STORAGE_ROUTE_IS_PURCHASE_VIEW, this.params['id']);
          } else {
            this.storageService.remove(RouteService.STORAGE_ROUTE_IS_PURCHASE_VIEW);
          }
        }
      }
    });
    router
      .events
      .pipe(filter(e => e instanceof NavigationEnd))
      .pipe(map(() => {
        let route = this.activatedRoute.firstChild;
        let child = route;

        while (child) {
          if (child.firstChild) {
            child = child.firstChild;
            route = child;
          } else {
            child = null;
          }
        }
        return route;
      }))
      .pipe(mergeMap(route => route.params))
      .subscribe(params => {
        this.currentParams = params;
      });
    router.onSameUrlNavigation = 'reload';
  }

  get url(): string {
    return this.currentUrl;
  }

  get prevUrl(): string {
    return this.previousUrl;
  }

  get params(): Params {
    return this.currentParams;
  }

  get queryParams(): Observable<Params> {
    return this.activatedRoute.queryParams;
  }

  checkRoute(params: string[] = [], url = this.url, depth = 2): boolean {
    let found = true;
    const routes = url && url.split('/') || [];
    params.forEach((param, index) => {
      const route = routes[index + depth];
      if (!param.startsWith(':') && (!route || route !== param)) {
        found = false;
      }
    });
    return found;
  }

  get isResetPassword(): boolean {
    return this.url && this.url.split('/')[1] === 'reset-password';
  }

  get isWindow(): boolean {
    return this.url && this.url.split('/')[1] === 'window';
  }

  openInExternalWindow(): void {
    const newUrl = cloneDeep(this.router.url).split('/');
    newUrl[1] = 'window';
    this.windowReference = window.open(newUrl.join('/'), 'externalWindow', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no');
    this.windowReference.moveTo(screen.availWidth * window.devicePixelRatio + 1, 0);
    this.storageService.set(RouteService.STORAGE_ROUTE_IS_PURCHASE_VIEW, this.params['id']);
    this.isWindowOpen = true;
  }

  reloadExternalWindowIfOpen(): void {
    if (this.windowReference && !this.windowReference.closed && !this.isPreviousEventOpened()) {
      this.openInExternalWindow();
    }
  }

  navigate(url: string, extras?: NavigationExtras): Promise<boolean> {
    this.updatePrevUrl();
    return this.router.navigate([url], extras);
  }

  navigateWithQueryParams(url: string, extras?: NavigationExtras): Promise<boolean> {
    const urlTree = this.router.parseUrl(url);
    return this.router.navigate(urlTree.root.children.primary.segments.map(s => s.path), {queryParams: urlTree.queryParams});
  }

  openNewTab(url: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.click();
  }

  download(url: string, navigateTo = ''): void {
    this.router.navigate([navigateTo ? navigateTo : this.previousUrl]).then(() => {
      window.location.href = url;
    });
  }

  refresh(url: string): void {
    this.router.navigate(['/window/blank']).then(() => {
      this.router.navigate([url]);
    });
  }

  updatePrevUrl(): void {
    this.previousUrl = cloneDeep(this.router.url);
  }

  replaceUrl(url: string, extras?: NavigationExtras): Promise<boolean> {
    this.updatePrevUrl();
    return this.router.navigate([url], {
      replaceUrl: true,
      ...extras
    });
  }

  back(): void {
    this.location.back();
  }

  isNavigationToSameUrl(): boolean {
    return this.url === this.prevUrl;
  }

  private isPreviousEventOpened(): boolean {
    return this.params['id'] === this.windowReference.location.href.split('/')[7];
  }
}
